export const AI_STATES = [
  "queued",
  "searching",
  "generating",
  "completed",
  "failed",
  "handoff",
] as const;

export type AiState = typeof AI_STATES[number];
export type ResponseMode = "ai" | "manual";
export type AiRunId = string;

export const LEGACY_AI_RUN_ID = "__legacy__";
export const MAX_TRACKED_AI_RUNS = 32;

export const isAiState = (value: unknown): value is AiState =>
  typeof value === "string" && (AI_STATES as readonly string[]).includes(value);

export const isAiActiveState = (value: unknown): value is AiState =>
  value === "queued" || value === "searching" || value === "generating";

export const normalizeAiState = (value: unknown): AiState | null =>
  value === null || value === undefined || value === ""
    ? null
    : isAiState(value)
    ? value
    : null;

export const normalizeResponseMode = (value: unknown): ResponseMode =>
  value === "ai" ? "ai" : "manual";

export type AiStatusEvent = {
  event?: string;
  thread_id?: string | number;
  run_id?: string | number;
  state?: unknown;
  status?: unknown;
};

export type AiStateSnapshot = {
  status?: AiState | null;
  state?: AiState | null;
  run_id?: string | number | null;
  source_message_id?: string | number | null;
  response_action?: string | null;
  source_refs?: unknown;
  updated_at?: string | null;
};

export type AiRunStatus = {
  state: AiState | null;
  sequence: number;
  terminal: boolean;
};

export type AiRunTracker = {
  runs: Record<AiRunId, AiRunStatus>;
  nextSequence: number;
};

/**
 * Returns undefined for unrelated or malformed events so callers can retain
 * the last known server state. A valid null state explicitly clears it.
 */
export const getAiStateFromEvent = (
  event: AiStatusEvent | null | undefined,
): AiState | null | undefined => {
  if (!event || event.event !== "ai_status") {
    return undefined;
  }

  const state =
    "state" in event && event.state !== undefined ? event.state : event.status;
  if (state === null) return null;
  return isAiState(state) ? state : undefined;
};

const getRunId = (event: AiStatusEvent): AiRunId => {
  if (event.run_id === undefined || event.run_id === null) {
    return LEGACY_AI_RUN_ID;
  }

  const runId = String(event.run_id).trim();
  return runId || LEGACY_AI_RUN_ID;
};

const isTerminalState = (state: AiState | null) =>
  state === null ||
  state === "completed" ||
  state === "failed" ||
  state === "handoff";

const trimRuns = (runs: Record<AiRunId, AiRunStatus>) => {
  const entries = Object.entries(runs);
  if (entries.length <= MAX_TRACKED_AI_RUNS) return runs;

  const activeEntries = entries
    .filter(([, status]) => !status.terminal)
    .sort(([, a], [, b]) => b.sequence - a.sequence);
  const terminalEntries = entries
    .filter(([, status]) => status.terminal)
    .sort(([, a], [, b]) => b.sequence - a.sequence);
  const retained = [...activeEntries, ...terminalEntries].slice(
    0,
    MAX_TRACKED_AI_RUNS,
  );

  // Keep the legacy slot when present so old clients cannot reopen a run
  // after its terminal event merely because newer explicit runs arrived.
  const legacyEntry = entries.find(([id]) => id === LEGACY_AI_RUN_ID);
  if (legacyEntry && !retained.some(([id]) => id === LEGACY_AI_RUN_ID)) {
    retained[retained.length - 1] = legacyEntry;
  }

  return Object.fromEntries(retained);
};

const isAiStateSnapshot = (value: unknown): value is AiStateSnapshot =>
  !!value && typeof value === "object" && !Array.isArray(value);

const getSnapshotState = (snapshot: unknown) => {
  if (!isAiStateSnapshot(snapshot)) return snapshot;
  return snapshot.status === undefined ? snapshot.state : snapshot.status;
};

const getSnapshotRunId = (snapshot: unknown): AiRunId => {
  if (!isAiStateSnapshot(snapshot)) return LEGACY_AI_RUN_ID;
  if (snapshot.run_id === undefined || snapshot.run_id === null) {
    return LEGACY_AI_RUN_ID;
  }

  const runId = String(snapshot.run_id).trim();
  return runId || LEGACY_AI_RUN_ID;
};

export const createAiRunTracker = (snapshot?: unknown): AiRunTracker => {
  const normalizedState = normalizeAiState(getSnapshotState(snapshot));
  if (normalizedState === null) {
    return { runs: {}, nextSequence: 1 };
  }

  const runId = getSnapshotRunId(snapshot);

  return {
    runs: {
      [runId]: {
        state: normalizedState,
        sequence: 1,
        terminal: isTerminalState(normalizedState),
      },
    },
    nextSequence: 2,
  };
};

export const applyAiStatusEvent = (
  current: AiRunTracker,
  event: AiStatusEvent | null | undefined,
): AiRunTracker => {
  const nextState = getAiStateFromEvent(event);
  if (nextState === undefined || !event) return current;

  const runId = getRunId(event);
  const hasExplicitRun = Object.keys(current.runs).some(
    (id) => id !== LEGACY_AI_RUN_ID,
  );

  // Once the server has identified a run, an old payload without run_id is
  // no longer attributable safely and must not reopen the spinner.
  if (runId === LEGACY_AI_RUN_ID && hasExplicitRun) return current;

  const runsWithoutLegacy =
    runId !== LEGACY_AI_RUN_ID && current.runs[LEGACY_AI_RUN_ID]
      ? Object.fromEntries(
          Object.entries(current.runs).filter(
            ([id]) => id !== LEGACY_AI_RUN_ID,
          ),
        )
      : current.runs;
  const previous = runsWithoutLegacy[runId];

  // A terminal event is a watermark for that run. Late active events from
  // the same run must never reopen it, while another run stays independent.
  if (previous?.terminal && !isTerminalState(nextState)) return current;

  const sequence = current.nextSequence;
  const runs = trimRuns({
    ...runsWithoutLegacy,
    [runId]: {
      state: nextState,
      sequence,
      terminal: isTerminalState(nextState),
    },
  });

  return { runs, nextSequence: sequence + 1 };
};

export const getDisplayedAiState = (tracker: AiRunTracker): AiState | null => {
  const latest = Object.values(tracker.runs).sort(
    (a, b) => b.sequence - a.sequence,
  );
  const active = latest.find((status) => !status.terminal);
  if (active) return active.state;

  return latest[0]?.state ?? null;
};

export const hasActiveAiRun = (tracker: AiRunTracker) =>
  Object.values(tracker.runs).some((status) => !status.terminal);

export const applyAiStateSnapshot = (state?: unknown): AiRunTracker =>
  createAiRunTracker(state);

export const terminalAiMessage = (state: AiState | null | undefined) => {
  if (state === "failed") {
    return "ビズAIが回答を作成できませんでした。運営担当者が確認します。";
  }
  if (state === "handoff") {
    return "ビズAIから運営担当者へ引き継ぎました。";
  }
  return null;
};
